import type { TypedSupabaseClient } from '../client';
import type { IPushTokenRepository, RegisterPushTokenInput } from '../../../interfaces/src';

const anyFrom = (client: TypedSupabaseClient) => (table: string) =>
  (client as unknown as { from: (t: string) => any }).from(table);

/**
 * Persists Expo device push tokens for the signed-in user.
 *
 * `register` goes through the `register_push_token` SECURITY DEFINER RPC so a
 * single Expo token always re-maps to its *current* owner — even when the same
 * physical device is re-used by another user/tenant after a re-login. A plain
 * upsert would hit an RLS wall when the conflicting row belongs to another
 * tenant; the RPC reassigns ownership atomically server-side.
 *
 * `unregister` is a plain RLS-scoped delete — a user can only remove their own
 * tokens, which is exactly the desired semantics on logout.
 */
export class SupabasePushTokenRepository implements IPushTokenRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async register(input: RegisterPushTokenInput): Promise<void> {
    const token = input.token?.trim();
    if (!token) throw new Error('push_token_required');
    const client = this.supabase as unknown as {
      rpc: (name: string, args: Record<string, unknown>) => Promise<{ error: unknown }>;
    };
    const { error } = await client.rpc('register_push_token', {
      p_token: token,
      p_platform: input.platform,
      p_device_name: input.deviceName ?? null,
      p_app_version: input.appVersion ?? null,
    });
    if (error) throw error;
  }

  async unregister(token: string): Promise<void> {
    const t = token?.trim();
    if (!t) return;
    const { error } = await anyFrom(this.supabase)('push_tokens').delete().eq('token', t);
    if (error) throw error;
  }
}
